import { useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import {
  ArrowLeft, KeyRound, Search, CheckCircle2, Clock, XCircle, Tent, Car, Calendar, MapPin, Users, ShieldCheck, Lock, Copy,
} from "lucide-react";
import { Logo } from "../components/common/Logo";
import { LandingHeader } from "../components/landing/LandingHeader";
import {
  BrutalButton, BrutalCard, BrutalInput, BrutalLabel, Badge, SectionTitle,
} from "../components/common/Brutal";
import { api, peso } from "../lib/api";

const STATUS_META = {
  unconfirmed: { color: "amber", icon: Clock, label: "UNCONFIRMED", desc: "We received your payment submission. Our team is verifying it." },
  confirmed: { color: "green", icon: CheckCircle2, label: "CONFIRMED", desc: "Your booking is locked in. QR passes are active below." },
  cancelled: { color: "red", icon: XCircle, label: "CANCELLED", desc: "This booking has been cancelled." },
};

export default function Status() {
  const [form, setForm] = useState({ booking_id: "", passcode: "" });
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!form.booking_id || !form.passcode) return toast.error("Booking ID and passcode required");
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/status", {
        booking_id: form.booking_id.toUpperCase(),
        passcode: form.passcode,
      });
      setBooking(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed");
      setBooking(null);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    const link = `${window.location.origin}/status?b=${booking.booking_id}&p=${form.passcode}`;
    navigator.clipboard.writeText(link);
    toast.success("Shareable link copied");
  };

  return (
    <div className="min-h-screen bg-[#FFF4E6] kf-grain">
      <LandingHeader />

      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <Badge color="magenta">Public Booking Status</Badge>
          <h1 className="display text-4xl sm:text-5xl mt-3 text-[#3D1E6D]">Track your reservation</h1>
          <p className="text-[#6B4E9E] mt-2 max-w-xl mx-auto">
            No login needed. Enter your booking ID and 10-digit passcode to see real-time payment status, QR pass availability, and check-in info.
          </p>
        </div>

        <BrutalCard className="p-6 max-w-xl mx-auto" data-testid="status-lookup-card">
          <form onSubmit={lookup} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div>
              <BrutalLabel>Booking ID</BrutalLabel>
              <BrutalInput value={form.booking_id} onChange={(e) => setForm({...form, booking_id: e.target.value.toUpperCase()})} placeholder="KG-XXXXXXXX" data-testid="status-bookingid" />
            </div>
            <div>
              <BrutalLabel>Passcode</BrutalLabel>
              <BrutalInput value={form.passcode} onChange={(e) => setForm({...form, passcode: e.target.value.replace(/\D/g, "").slice(0, 10)})} placeholder="10-digit" data-testid="status-passcode" />
            </div>
            <BrutalButton type="submit" variant="primary" disabled={loading} data-testid="status-lookup">
              <Search className="h-4 w-4" /> {loading ? "..." : "Check"}
            </BrutalButton>
          </form>
        </BrutalCard>

        {booking && (
          <div className="mt-8 space-y-5" data-testid="status-result">
            <BrutalCard className="p-6">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#6B4E9E]">Booking</div>
                  <div className="display text-3xl text-[#3D1E6D]">{booking.booking_id}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(() => {
                      const m = STATUS_META[booking.payment_status] || STATUS_META.unconfirmed;
                      return <Badge color={m.color}>{m.label}</Badge>;
                    })()}
                    <Badge color="magenta">{booking.camping_type?.toUpperCase()}</Badge>
                    <Badge color="yellow">{booking.persons?.length || 0} persons</Badge>
                  </div>
                </div>
                <button onClick={copyLink} className="kf-border bg-[#FFB400] px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5" data-testid="status-copy-link">
                  <Copy className="h-3 w-3" /> Share link
                </button>
              </div>

              <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
                <Stat icon={MapPin} label="Zone" value={booking.zone_name} />
                <Stat icon={Calendar} label="Check-in" value={booking.check_in} />
                <Stat icon={Calendar} label="Check-out" value={booking.check_out} />
                <Stat icon={Tent} label="Nights" value={booking.nights} />
                <Stat icon={Users} label="Primary Camper" value={booking.persons?.[0]?.name} />
                <Stat icon={ShieldCheck} label="Total" value={peso(booking.total_amount)} />
              </div>

              {(() => {
                const m = STATUS_META[booking.payment_status] || STATUS_META.unconfirmed;
                const cls = booking.payment_status === "confirmed" ? "bg-[#10B981] text-white" : booking.payment_status === "cancelled" ? "bg-[#EF4444] text-white" : "bg-[#FFB400] text-[#3D1E6D]";
                return (
                  <div className={`mt-5 kf-border p-4 flex items-start gap-3 ${cls}`}>
                    <m.icon className="h-6 w-6 shrink-0" strokeWidth={2.75} />
                    <div className="text-sm">
                      <div className="font-bold display text-xl">{m.label}</div>
                      <div>{m.desc}</div>
                      {booking.payment_confirmed_at && (
                        <div className="text-xs mt-1 opacity-90">Confirmed on {new Date(booking.payment_confirmed_at).toLocaleString()}</div>
                      )}
                      {booking.payment_received_at && booking.payment_status === "unconfirmed" && (
                        <div className="text-xs mt-1 opacity-90">Payment received on {new Date(booking.payment_received_at).toLocaleString()}</div>
                      )}
                      {booking.cancellation_reason && (
                        <div className="text-xs mt-1 opacity-90">Reason: {booking.cancellation_reason}</div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </BrutalCard>

            {/* QR availability */}
            <BrutalCard className="p-6">
              <SectionTitle kicker="QR Passes" title="Gate access" />
              {booking.payment_status !== "confirmed" || booking.qr_invalid ? (
                <div className="mt-4 kf-border bg-[#FFB400] p-4 flex items-center gap-3" data-testid="status-qr-locked">
                  <Lock className="h-6 w-6 text-[#3D1E6D]" strokeWidth={2.75} />
                  <div className="text-sm text-[#3D1E6D]">
                    <div className="font-bold display text-xl">QR codes not yet available</div>
                    <p>QR passes appear once admin confirms your payment.</p>
                  </div>
                </div>
              ) : (
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {booking.persons.map((p) => (
                    <div key={p.id} className="kf-border bg-[#FFF4E6] p-4 text-center" data-testid={`status-qr-${p.id}`}>
                      <div className="bg-white kf-border p-3 inline-block">
                        <QRCodeCanvas value={p.id} size={140} bgColor="#ffffff" fgColor="#3D1E6D" level="H" />
                      </div>
                      <div className="display text-lg mt-2">{p.name}</div>
                      <div className="flex flex-wrap gap-1 justify-center mt-1">
                        {p.is_primary && <Badge color="magenta">Primary</Badge>}
                        <Badge color={p.status === "checked-in" ? "green" : p.status === "checked-out" ? "teal" : p.status === "overdue" ? "red" : "yellow"}>
                          {p.status}
                        </Badge>
                      </div>
                      {p.last_status_change && (
                        <div className="text-[10px] uppercase tracking-widest text-[#6B4E9E] mt-1">
                          Last update: {new Date(p.last_status_change).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ))}
                  {booking.camping_type === "car" && booking.car_details && (
                    <div className="kf-border bg-[#FFB400] p-4 text-center">
                      <div className="bg-white kf-border p-3 inline-block">
                        <QRCodeCanvas value={`car:${booking.id}`} size={140} bgColor="#ffffff" fgColor="#3D1E6D" level="H" />
                      </div>
                      <div className="display text-lg mt-2 flex items-center justify-center gap-1.5"><Car className="h-4 w-4" /> {booking.car_details.plate_number}</div>
                      <Badge color="magenta" className="mt-1">Vehicle Pass</Badge>
                    </div>
                  )}
                </div>
              )}
            </BrutalCard>

            {/* Check-in summary */}
            <BrutalCard className="p-6">
              <SectionTitle kicker="Check-in Summary" title="Who's on site" />
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FFB400]">
                    <tr>
                      {["Camper", "Status", "Last Update"].map((h) => (
                        <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest border-b-2 border-[#3D1E6D]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {booking.persons.map((p, i) => (
                      <tr key={p.id} className={i % 2 ? "bg-[#FFF4E6]" : "bg-white"}>
                        <td className="px-3 py-2 font-bold">{p.name} {p.is_primary && <Badge color="magenta" className="ml-1">Primary</Badge>}</td>
                        <td className="px-3 py-2"><Badge color={p.status === "checked-in" ? "green" : p.status === "checked-out" ? "teal" : p.status === "overdue" ? "red" : "yellow"}>{p.status}</Badge></td>
                        <td className="px-3 py-2 text-[#6B4E9E]">{p.last_status_change ? new Date(p.last_status_change).toLocaleString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </BrutalCard>
          </div>
        )}

        {!booking && (
          <div className="mt-8 text-center text-xs uppercase tracking-widest text-[#6B4E9E] flex items-center justify-center gap-2">
            <KeyRound className="h-3 w-3" />
            Your booking ID & passcode were sent on your reservation success page.
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="kf-border bg-[#FFF4E6] p-3 flex items-center gap-2">
    <Icon className="h-4 w-4 text-[#E5247C]" strokeWidth={2.75} />
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B4E9E]">{label}</div>
      <div className="font-bold text-sm">{value || "—"}</div>
    </div>
  </div>
);
